/**
 * Protein Pal composition engine.
 *
 * Extracted verbatim from `App.tsx` — the engine the lab and the label follow —
 * so that the other screens can share the same arithmetic instead of each
 * keeping a private copy. Nothing here is new maths. Every expression below is
 * the one App.tsx already evaluates; the line references say where.
 *
 * Pure: no React, no Supabase, no DOM. That is the point — it can be tested,
 * and `src/engine/__tests__/composition.test.ts` pins it to the numbers the
 * live app printed for F234 on 2026-07-29.
 *
 * Two basis rules this file exists to protect:
 *   1. A formula's components blend by the AS-MIXED component ratio, which for
 *      a `finished`-mode formula is back-calculated from the moisture targets —
 *      NOT by whatever the batch happened to be weighed at.
 *   2. Amino acids are mg per gram of PROTEIN, so they blend by each
 *      ingredient's share of the protein, not its share of the mass.
 */

/** Reference pattern, mg per g protein. WHO/FAO/UNU 2007, 2–5 year old. */
export const REFERENCE_PATTERN: Readonly<Record<string, number>> = {
  histidine: 19,
  isoleucine: 28,
  leucine: 66,
  lysine: 58,
  methionine_cysteine: 25,
  phenylalanine_tyrosine: 63,
  threonine: 34,
  tryptophan: 11,
  valine: 35,
};

/** Database column → reference-pattern key. */
export const AA_COLUMN_MAP: Readonly<Record<string, string>> = {
  aa_his: "histidine",
  aa_ile: "isoleucine",
  aa_leu: "leucine",
  aa_lys: "lysine",
  aa_mc: "methionine_cysteine",
  aa_pt: "phenylalanine_tyrosine",
  aa_thr: "threonine",
  aa_trp: "tryptophan",
  aa_val: "valine",
};

export interface ComponentRatioInput {
  /** Percentage the user entered. Its meaning depends on ratioMode. */
  doughRatioPct: number;
  /** "finished" means doughRatioPct describes the baked piece. */
  ratioMode: "as-mixed" | "finished";
  postProcessEnabled: boolean;
  /** Moisture of the as-mixed dough / filling, g per 100 g. */
  doughMoisturePct: number;
  fillingMoisturePct: number;
  moistureMode: "components" | "whole";
  /** Targets. null or negative means "no change" — App.tsx's -1 sentinel. */
  targetDoughMoisture?: number | null;
  targetFillingMoisture?: number | null;
  targetWholeMoisture?: number | null;
}

export interface ComponentRatio {
  asMixedDough: number;
  asMixedFilling: number;
}

/**
 * The as-mixed dough/filling split to blend a formula on.
 *
 * Mirrors `App.tsx` `effectiveRatios` (:1961-2007) expression for expression,
 * including the one-decimal rounding at :2000 and the 0–80 moisture clamp at
 * :1985 — both are load-bearing for reproducing the audit exactly.
 */
export function computeAsMixedRatio(input: ComponentRatioInput): ComponentRatio {
  const {
    doughRatioPct, ratioMode, postProcessEnabled,
    doughMoisturePct: dM, fillingMoisturePct: fM, moistureMode,
  } = input;

  // App.tsx:1972 — the entered ratio is already as-mixed.
  if (!postProcessEnabled || ratioMode === "as-mixed") {
    return { asMixedDough: doughRatioPct, asMixedFilling: 100 - doughRatioPct };
  }

  // App.tsx:1979-1980 — sentinel: absent/negative target means "no change".
  const rawDough = input.targetDoughMoisture;
  const rawFilling = input.targetFillingMoisture;
  let tDM = rawDough == null || rawDough < 0 ? dM : rawDough;
  let tFM = rawFilling == null || rawFilling < 0 ? fM : rawFilling;

  // App.tsx:1981-1988 — whole-product target scales both components.
  if (moistureMode === "whole" && input.targetWholeMoisture != null) {
    const compMoist = (dM * doughRatioPct + fM * (100 - doughRatioPct)) / 100;
    if (compMoist > 0) {
      const scale = input.targetWholeMoisture / compMoist;
      tDM = Math.max(0, Math.min(80, dM * scale));
      tFM = Math.max(0, Math.min(80, fM * scale));
    }
  }

  // App.tsx:1991-1992 — dry-matter concentration factors.
  const dC = 100 - dM > 0.01 ? (100 - tDM) / (100 - dM) : 1;
  const fC = 100 - fM > 0.01 ? (100 - tFM) / (100 - fM) : 1;

  // App.tsx:1994-2000 — solve the finished→as-mixed relation rather than
  // averaging it: asMixed = p*dC / (fC*(1-p) + p*dC).
  const p = doughRatioPct / 100;
  const denom = fC * (1 - p) + p * dC;
  const asMixedFrac = denom > 0 ? (p * dC) / denom : p;
  const rounded = Math.round(asMixedFrac * 1000) / 10;
  const asMixedDough = Math.max(0, Math.min(100, rounded));

  return { asMixedDough, asMixedFilling: 100 - asMixedDough };
}

export interface EngineRow {
  component: string;
  /** Fraction of its own component, 0–1, as stored in formula_components. */
  pct_of_component: number | null;
  protein_per_100g: number | null;
  digestibility: number | null;
  moisture?: number | null;
  fat?: number | null;
  carbs?: number | null;
  fiber?: number | null;
  [aaColumn: string]: unknown;
}

export interface BlendResult {
  /**
   * Components that carry a non-zero ratio but contain no ingredient rows.
   *
   * A formula declaring "50% dough" with no dough rows still gets blended at
   * 50%, so every nutrient comes out at half its true value and nothing says
   * so. Verified live 2026-07-29: F222 declares 19.8626 g protein per 100 g
   * where its filling alone is 39.7252. Screens should refuse to present a
   * result with this non-empty, or say plainly that it is wrong.
   */
  emptyComponents: string[];
  /** g per 100 g of as-mixed product. */
  totalProtein: number;
  weightedDigestibility: number;
  /** Blended amino acids, mg per g protein. */
  blendedAA: Record<string, number>;
  aminoAcidScore: number;
  limitingAA: string | null;
  pdcaas: number;
  truncatedPdcaas: number;
}

/**
 * Blend a formula's components on a given as-mixed ratio.
 *
 * Protein and digestibility follow App.tsx:2091-2097; amino acids follow
 * App.tsx:2100-2113, weighted by protein share. `doughRatio` and
 * `fillingRatio` are percentages and normally come from computeAsMixedRatio.
 */
export function blendComposition(
  rows: EngineRow[],
  doughRatio: number,
  fillingRatio: number,
): BlendResult {
  const ratioFor = (component: string) =>
    (component === "DOUGH" ? doughRatio : fillingRatio) / 100;

  const emptyComponents: string[] = [];
  for (const [component, ratio] of [["DOUGH", doughRatio], ["FILLING", fillingRatio]] as const) {
    const hasRows = rows.some((r) => r.component === component);
    if (!hasRows && ratio > 0) emptyComponents.push(component);
  }

  let totalProtein = 0;
  let weightedDigestibilitySum = 0;
  const proteinContribs: { protein: number; row: EngineRow }[] = [];

  for (const row of rows) {
    const share = (row.pct_of_component ?? 0) * ratioFor(row.component);
    const proteinContrib = share * (row.protein_per_100g ?? 0);
    if (proteinContrib === 0) continue;
    totalProtein += proteinContrib;
    weightedDigestibilitySum += proteinContrib * (row.digestibility ?? 0);
    proteinContribs.push({ protein: proteinContrib, row });
  }

  const weightedDigestibility = totalProtein > 0 ? weightedDigestibilitySum / totalProtein : 0;

  const blendedAA: Record<string, number> = {};
  for (const aa of Object.keys(REFERENCE_PATTERN)) blendedAA[aa] = 0;
  if (totalProtein > 0) {
    for (const { protein, row } of proteinContribs) {
      const fraction = protein / totalProtein;
      for (const [column, aa] of Object.entries(AA_COLUMN_MAP)) {
        blendedAA[aa] += ((row[column] as number | null) ?? 0) * fraction;
      }
    }
  }

  let limitingAA: string | null = null;
  let limitingScore = Infinity;
  for (const [aa, ref] of Object.entries(REFERENCE_PATTERN)) {
    const score = ref > 0 ? blendedAA[aa] / ref : 0;
    if (score < limitingScore) {
      limitingScore = score;
      limitingAA = aa;
    }
  }

  const aminoAcidScore = Math.min(limitingScore, 1);
  const pdcaas = aminoAcidScore * weightedDigestibility;

  return {
    emptyComponents,
    totalProtein,
    weightedDigestibility,
    blendedAA,
    aminoAcidScore,
    limitingAA,
    pdcaas,
    truncatedPdcaas: Math.min(pdcaas, 1),
  };
}

/** Moisture of one component, g per 100 g, from its own rows. */
export function componentMoisture(rows: EngineRow[], component: string): number {
  return rows
    .filter((r) => r.component === component)
    .reduce((sum, r) => sum + (r.pct_of_component ?? 0) * (r.moisture ?? 0), 0);
}
